import React, { useState, useRef, useEffect } from 'react';
import { Tag, Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

export const BundleStickerStep = ({ bundleCode, canChange, onLocked, onChange }) => {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { if (!bundleCode) setTimeout(() => inputRef.current?.focus(), 80); }, [bundleCode]);

  const submit = async (e) => {
    e.preventDefault();
    const code = value.trim();
    if (!code || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await apiClient.post('/wms/sfg/floor-clearance/validate-bundle', { bundle_code: code });
      onLocked(res.data.bundle_code);
      setValue('');
      toast.success(`Bundle sticker ${res.data.bundle_code} accepted`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid bundle sticker');
      setValue('');
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white rounded-xl border border-[#E5DFD0] p-4" data-testid="fc-bundle-step">
      <div className="flex items-center gap-2 mb-3">
        <span className="h-6 w-6 rounded-full bg-[#1A1F2E] text-white text-xs font-bold flex items-center justify-center">1</span>
        <Tag className="h-4 w-4 text-[#2D5F4F]" />
        <h2 className="text-sm font-semibold text-[#1A1F2E]">Scan the new bundle sticker</h2>
      </div>
      {bundleCode ? (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 bg-[#2D5F4F] text-white px-4 py-2 rounded-lg font-mono text-base" data-testid="fc-bundle-locked">
            <Lock className="h-4 w-4" /> {bundleCode}
          </div>
          <Button variant="outline" size="sm" disabled={!canChange} onClick={onChange} data-testid="fc-bundle-change-btn">
            Change
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="flex gap-2">
          <Input ref={inputRef} value={value} onChange={(e) => setValue(e.target.value)} disabled={busy}
            placeholder="Scan pre-pasted bundle sticker (BUNxxxxxx/YY-YY)" className="font-mono" data-testid="fc-bundle-input" />
          <Button type="submit" disabled={busy || !value.trim()} className="bg-[#2D5F4F] hover:bg-[#244d40]" data-testid="fc-bundle-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Use'}
          </Button>
        </form>
      )}
      {error && <p className="text-sm text-red-600 mt-2" data-testid="fc-bundle-error">{error}</p>}
    </section>
  );
};
